import type {
  IAutoReviewMissingField,
  IPendingLeadAutoReviewInput,
} from '@user/store/draftLeads/typings'
import { customValuesToInputModel } from '@user/components/CustomFields/Editable/mapper'
import { UNASSIGNED_OWNER_ID } from '@user/components/Settings/LeadAssignmentRule/constants'

import type {
  AutoReviewLookups,
  NamedOption,
  UpdateRequiredFieldsFormModel,
} from '../typings'
import { groupMissingFields } from './groupMissingFields'

const filled = (value: string | null | undefined): value is string =>
  (value ?? '').trim() !== ''

const nameOf = (options: NamedOption[], id: string) =>
  options.find((o) => o.id === id)?.name

/**
 * Builds the auto-review request body: only fields the server reported missing and that have a
 * value. An id is emitted together with its resolved name (branch, division, location type,
 * campaign, source), and omitted entirely when the name cannot be resolved, because the server
 * rejects an id without a name.
 */
export function buildAutoReviewInput(
  values: UpdateRequiredFieldsFormModel,
  missing: IAutoReviewMissingField[],
  lookups: AutoReviewLookups,
): IPendingLeadAutoReviewInput {
  const input: IPendingLeadAutoReviewInput = {}
  const customFieldIds = new Set<number>()

  const pair = (
    id: string,
    options: NamedOption[],
    set: (id: string, name: string) => void,
  ) => {
    if (!filled(id)) return
    const name = nameOf(options, id)
    if (name) set(id, name)
  }

  groupMissingFields(missing).forEach((group) => {
    switch (group.kind) {
      case 'nameOrCompany':
        if (filled(values.firstName)) input.firstName = values.firstName
        if (filled(values.lastName)) input.lastName = values.lastName
        if (filled(values.company)) input.company = values.company
        break
      case 'phoneOrEmail':
        if (filled(values.phoneNumber)) input.phoneNumber = values.phoneNumber
        if (filled(values.emailAddress))
          input.emailAddress = values.emailAddress
        break
      case 'branchId':
        pair(values.branchId, lookups.branches, (id, name) => {
          input.branchId = id
          input.branch = name
        })
        break
      case 'assignment':
        group.fields.forEach((f) => {
          if (f.fieldKey === 'salesTeamId' && values.salesTeamId)
            input.salesTeamId = values.salesTeamId
          if (f.fieldKey === 'assigneeId' && filled(values.assigneeId))
            input.assigneeId =
              values.assigneeId === UNASSIGNED_OWNER_ID
                ? null
                : values.assigneeId
          if (f.fieldKey === 'salesFunnelId' && values.salesFunnelId)
            input.salesFunnelId = values.salesFunnelId
          if (f.fieldKey === 'salesFunnelStageId' && values.salesFunnelStageId)
            input.salesFunnelStageId = values.salesFunnelStageId
        })
        break
      case 'additional':
        switch (group.key) {
          case 'divisionId':
            pair(values.divisionId, lookups.divisions, (id, name) => {
              input.divisionId = id
              input.division = name
            })
            break
          case 'altPhoneNumber':
            if (filled(values.altPhoneNumber))
              input.altPhoneNumber = values.altPhoneNumber
            break
          case 'address':
            if (filled(values.street1)) input.street1 = values.street1
            if (filled(values.street2)) input.street2 = values.street2
            if (filled(values.city)) input.city = values.city
            if (filled(values.stateAbbreviation))
              input.stateAbbreviation = values.stateAbbreviation
            if (filled(values.postalCode)) input.postalCode = values.postalCode
            break
          case 'locationTypeId':
            pair(values.locationTypeId, lookups.locationTypes, (id, name) => {
              input.locationTypeId = id
              input.locationType = name
            })
            break
          case 'note':
            if (filled(values.note)) input.note = values.note
            break
          case 'targetPests':
            if (values.targetPestIds?.length > 0)
              input.targetPestIds = values.targetPestIds
            break
          case 'campaignId':
            pair(values.campaignId, lookups.campaigns, (id, name) => {
              input.campaignId = id
              input.campaign = name
            })
            break
          case 'leadSourceId':
            pair(values.sourceId, lookups.sources, (id, name) => {
              input.sourceId = id
              input.source = name
            })
            break
          case 'referral':
            if (filled(values.referralCode))
              input.referralCode = values.referralCode
            if (filled(values.referringBillTo))
              input.referringBillTo = values.referringBillTo
            break
        }
        break
      case 'custom':
        customFieldIds.add(group.fieldId)
        break
      default:
        break
    }
  })

  if (customFieldIds.size > 0) {
    const customFieldValues = customValuesToInputModel(
      values,
      lookups.customFields.filter((f) => customFieldIds.has(f.id)),
    )
    if (customFieldValues.length > 0)
      input.customFieldValues = customFieldValues
  }

  return input
}
